import { useEffect, useState } from "react";
import api, { formatError, downloadReport } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { roleOf } from "@/lib/roles";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ListToolbar } from "@/components/ListToolbar";
import {
  Plus, CheckCircle2, XCircle, AlertCircle, FileWarning, Sparkles, Loader2,
  Upload, Download, ShieldAlert, FileText, Gauge, Link as LinkIcon,
} from "lucide-react";

const STATUS = {
  awaiting_documents: { c: "bg-amber-100 text-amber-800", l: "Awaiting Documents" },
  completeness_check: { c: "bg-blue-100 text-blue-800", l: "Ready for Review" },
  admitted: { c: "bg-emerald-100 text-emerald-800", l: "Admitted" },
  rejected: { c: "bg-red-100 text-red-800", l: "Rejected" },
};

const REC = {
  admit: { c: "bg-emerald-100 text-emerald-700", l: "Recommend: Admit" },
  conditional_admit: { c: "bg-teal-100 text-teal-700", l: "Recommend: Conditional Admit" },
  request_info: { c: "bg-amber-100 text-amber-800", l: "Recommend: Request Info" },
  decline: { c: "bg-red-100 text-red-700", l: "Recommend: Decline" },
};

const DOC_TYPES = ["CV", "Degree Certificate", "Academic Transcript", "ID / Passport", "Motivation Letter", "Other"];
const REC_TO_DECISION = { admit: "admit", conditional_admit: "admit", request_info: "request_info", decline: "reject" };
const COHORT_TEMPLATE = "data:text/csv;charset=utf-8," + encodeURIComponent("applicant_name,email,programme\nJane Doe,jane.doe@example.com,MSc Public Health\nJohn Smith,john.smith@example.com,PhD Education\n");

export default function Admissions() {
  const { user } = useAuth();
  const role = roleOf(user);
  const canReview = ["tenant_admin", "staff", "registrar", "super_admin"].includes(role);
  const [apps, setApps] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ applicant_name: "", applicant_email: "", programme: "" });
  const [selected, setSelected] = useState(null);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const load = () => api.get("/applications").then((r) => setApps(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const runImport = async () => {
    if (!importFile) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", importFile);
      fd.append("base_url", window.location.origin);
      const { data } = await api.post("/applications/bulk", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImportResult(data);
      toast.success(`${data.created} application(s) created`);
      setImportFile(null);
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setImporting(false); }
  };

  const submit = async () => {
    try {
      await api.post("/applications", { ...form, documents: [], base_url: window.location.origin });
      toast.success("Application created — an upload link was emailed to the applicant");
      setOpen(false);
      setForm({ applicant_name: "", applicant_email: "", programme: "" });
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Admissions & Enquiry</h1>
          <p className="text-muted-foreground">Upload documents, run an AI eligibility review, and record the Registrar's decision.</p>
        </div>
        <div className="flex items-center gap-2">
        {canReview && (
          <Dialog open={importOpen} onOpenChange={(v) => { setImportOpen(v); if (!v) setImportResult(null); }}>
            <DialogTrigger asChild>
              <Button data-testid="import-cohort-btn" variant="outline"><Upload className="h-4 w-4 mr-1" />Import cohort</Button>
            </DialogTrigger>
            <DialogContent className="bg-white">
              <DialogHeader><DialogTitle>Import a cohort of applicants</DialogTitle>
                <DialogDescription>Upload a CSV or Excel (.xlsx) file with columns <strong>applicant_name</strong>, <strong>email</strong> and <strong>programme</strong>. Each valid row creates an application and emails the applicant their document-upload link.</DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <a data-testid="cohort-template" href={COHORT_TEMPLATE} download="cohort-template.csv" className="text-sm brand-text underline inline-flex items-center gap-1"><Download className="h-3.5 w-3.5" />Download CSV template</a>
                <input data-testid="cohort-file" type="file" accept=".csv,.xlsx" onChange={(e) => setImportFile(e.target.files?.[0] || null)} className="block w-full text-sm file:mr-3 file:rounded-md file:border-0 file:bg-[rgb(var(--brand))] file:text-white file:px-3 file:py-1.5 file:text-sm cursor-pointer" />
                {importResult && (
                  <div data-testid="cohort-result" className="rounded-md border border-border p-3 text-sm space-y-1">
                    <p className="font-medium text-emerald-700">{importResult.created} application(s) created from {importResult.total_rows} row(s)</p>
                    {importResult.skipped > 0 && <p className="text-amber-700">{importResult.skipped} row(s) skipped</p>}
                    {(importResult.errors || []).slice(0, 6).map((er, idx) => <p key={idx} className="text-xs text-muted-foreground">Row {er.row}: {er.reason}</p>)}
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button data-testid="cohort-import-submit" onClick={runImport} disabled={!importFile || importing} className="brand-bg text-white">
                  {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Upload className="h-4 w-4 mr-1" />Import</>}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-application-btn" className="brand-bg text-white hover:opacity-90">
              <Plus className="h-4 w-4 mr-1" /> New Application
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white">
            <DialogHeader><DialogTitle>New Application</DialogTitle>
              <DialogDescription>Create the case, then open it to upload documents.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div><Label>Applicant name</Label><Input data-testid="app-name" value={form.applicant_name} onChange={(e) => setForm({ ...form, applicant_name: e.target.value })} /></div>
              <div><Label>Email</Label><Input data-testid="app-email" type="email" value={form.applicant_email} onChange={(e) => setForm({ ...form, applicant_email: e.target.value })} /></div>
              <div><Label>Programme</Label><Input data-testid="app-programme" value={form.programme} onChange={(e) => setForm({ ...form, programme: e.target.value })} /></div>
            </div>
            <DialogFooter><Button data-testid="app-submit" onClick={submit} disabled={!form.applicant_name || !form.applicant_email || !form.programme} className="brand-bg text-white">Create</Button></DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <div className="grid gap-3" data-testid="applications-list">
        {apps.length > 0 && (
          <ListToolbar
            testid="admissions" search={q} onSearch={setQ}
            placeholder="Search by applicant, email or programme…"
            filters={[{
              key: "status", value: statusFilter, onChange: setStatusFilter, label: "Status",
              options: [{ value: "all", label: "All statuses" }, ...Object.entries(STATUS).map(([v, o]) => ({ value: v, label: o.l }))],
            }]}
          />
        )}
        {apps
          .filter((a) => statusFilter === "all" || a.status === statusFilter)
          .filter((a) => {
            const t = q.trim().toLowerCase();
            return !t || [a.applicant_name, a.applicant_email, a.programme].some((x) => (x || "").toLowerCase().includes(t));
          })
          .map((a) => {
            const s = STATUS[a.status] || { c: "bg-slate-100 text-slate-700", l: a.status };
            const rec = a.ai_recommendation && REC[a.ai_recommendation];
            return (
              <Card key={a.id} className="p-5 hover:shadow-sm transition-shadow cursor-pointer" data-testid={`app-${a.id}`} onClick={() => setSelected(a)}>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-heading font-semibold">{a.applicant_name}</h3>
                      <Badge className={s.c}>{s.l}</Badge>
                      {rec && <Badge className={`${rec.c} gap-1`} data-testid={`app-rec-${a.id}`}><Sparkles className="h-3 w-3" />{rec.l}</Badge>}
                      {a.ai_status === "analyzing" && <Badge className="bg-blue-100 text-blue-700 gap-1"><Loader2 className="h-3 w-3 animate-spin" />AI reviewing</Badge>}
                    </div>
                    <p className="text-sm text-muted-foreground">{a.programme} · {a.applicant_email}</p>
                    <div className="flex items-center gap-3 mt-2 text-xs">
                      <span className="flex items-center gap-1 text-muted-foreground"><FileText className="h-3 w-3" />{(a.document_files || []).length} document(s)</span>
                      {a.ai_report?.risk_flags?.length > 0 && (
                        <span className="flex items-center gap-1 text-red-700"><ShieldAlert className="h-3 w-3" />{a.ai_report.risk_flags.length} risk flag(s)</span>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        {apps.length === 0 && <p className="text-muted-foreground text-sm">No applications yet.</p>}
      </div>

      {selected && (
        <ApplicationDetail
          appId={selected.id} canReview={canReview} onClose={() => setSelected(null)} onChanged={load}
        />
      )}
    </div>
  );
}

function ApplicationDetail({ appId, canReview, onClose, onChanged }) {
  const [a, setA] = useState(null);
  const [docType, setDocType] = useState("CV");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [decision, setDecision] = useState("");
  const [notes, setNotes] = useState("");
  const [supervisors, setSupervisors] = useState([]);
  const [supervisorId, setSupervisorId] = useState("");
  const [suggesting, setSuggesting] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [autoTried, setAutoTried] = useState(false);

  const refresh = async () => {
    const { data } = await api.get(`/applications/${appId}`);
    setA(data);
    return data;
  };
  useEffect(() => { refresh(); }, [appId]); // eslint-disable-line
  useEffect(() => { if (canReview) api.get("/supervisors").then((r) => setSupervisors(r.data)).catch(() => {}); }, [canReview]);
  // poll while AI is analyzing
  useEffect(() => {
    if (a?.ai_status !== "analyzing") return;
    const t = setInterval(async () => { const d = await refresh(); if (d.ai_status !== "analyzing") clearInterval(t); }, 4000);
    return () => clearInterval(t);
  }, [a?.ai_status]); // eslint-disable-line
  useEffect(() => {
    if (a?.ai_recommendation && !decision && !a?.decided_by) setDecision(REC_TO_DECISION[a.ai_recommendation] || "");
  }, [a?.ai_recommendation]); // eslint-disable-line
  // AI auto-allocate: when Admit is chosen, fetch suggestions once and pre-select the top match
  useEffect(() => {
    if (decision === "admit" && canReview && supervisors.length && !suggestions.length && !autoTried && !a?.decided_by) {
      setAutoTried(true);
      suggestSup({ auto: true });
    }
  }, [decision, canReview, supervisors.length, a?.decided_by]); // eslint-disable-line

  const upload = async () => {
    if (!file) { toast.error("Choose a file first"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("doc_type", docType); fd.append("file", file);
      await api.post(`/applications/${appId}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded");
      setFile(null);
      await refresh(); onChanged();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const runAi = async () => {
    try {
      await api.post(`/applications/${appId}/ai-review`);
      toast.success("AI eligibility review started");
      await refresh();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const decide = async () => {
    try {
      const { data } = await api.patch(`/applications/${appId}/decision`, {
        decision, notes: notes || `${decision} by reviewer`, missing_items: [],
        supervisor_id: decision === "admit" && supervisorId ? supervisorId : null,
        base_url: window.location.origin,
      });
      toast.success("Decision recorded");
      if (data.supervision_id) toast.success("Supervision created & supervisor allocated");
      if (data.student_temp_password) toast.info(`Student login created — temp password: ${data.student_temp_password} (a welcome email was sent)`, { duration: 12000 });
      await refresh(); onChanged();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const suggestSup = async ({ auto = false } = {}) => {
    setSuggesting(true);
    try {
      const { data } = await api.get(`/applications/${appId}/suggest-supervisor`);
      const list = data.suggestions || [];
      setSuggestions(list);
      if (auto && list.length) {
        setSupervisorId((cur) => cur || list[0].supervisor_id);
      } else if (!auto && !list.length) {
        toast.info("No supervisor suggestions available.");
      }
    } catch (e) { if (!auto) toast.error(formatError(e.response?.data?.detail)); }
    finally { setSuggesting(false); }
  };

  const r = a?.ai_report;
  const decided = !!a?.decided_by;

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-white max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="app-detail">
        {!a ? <div className="flex justify-center py-10">
          <DialogHeader className="sr-only"><DialogTitle>Application</DialogTitle></DialogHeader>
          <Loader2 className="h-6 w-6 animate-spin brand-text" />
        </div> : (
          <>
            <DialogHeader>
              <DialogTitle className="pr-8">{a.applicant_name}</DialogTitle>
              <DialogDescription>{a.programme} · {a.applicant_email}</DialogDescription>
            </DialogHeader>

            {/* Documents */}
            <div className="rounded-lg border border-border p-4 space-y-3">
              <p className="font-heading font-semibold text-sm flex items-center gap-2"><FileText className="h-4 w-4 brand-text" />Documents</p>
              <div className="space-y-1" data-testid="app-doc-list">
                {(a.document_files || []).map((d) => (
                  <div key={d.file_id} className="flex items-center justify-between text-sm rounded-md bg-secondary/40 px-3 py-2">
                    <span className="flex items-center gap-2"><Badge variant="outline" className="text-[11px]">{d.doc_type}</Badge>{d.original_filename}</span>
                    <a href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${d.file_id}/download`} target="_blank" rel="noreferrer" className="brand-text hover:underline flex items-center gap-1"><Download className="h-3.5 w-3.5" />Open</a>
                  </div>
                ))}
                {(a.document_files || []).length === 0 && <p className="text-sm text-muted-foreground">No documents uploaded yet.</p>}
              </div>
              {canReview && !decided && (
                <div className="flex flex-col sm:flex-row gap-2 sm:items-end pt-2 border-t border-border">
                  <div className="w-full sm:w-44">
                    <Label className="text-xs">Document type</Label>
                    <Select value={docType} onValueChange={setDocType}>
                      <SelectTrigger data-testid="app-doc-type"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white">{DOC_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <input data-testid="app-doc-file" type="file" accept=".pdf,.docx,.txt,.md" onChange={(e) => setFile(e.target.files?.[0] || null)} className="flex-1 text-sm file:mr-3 file:rounded-md file:border-0 file:bg-[rgb(var(--brand))] file:text-white file:px-3 file:py-1.5 file:text-sm cursor-pointer" />
                  <Button data-testid="app-doc-upload" onClick={upload} disabled={uploading} variant="outline">
                    {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Upload className="h-4 w-4 mr-1" />Upload</>}
                  </Button>
                </div>
              )}
            </div>

            {/* AI review */}
            {canReview && (
              <div className="flex items-center justify-between gap-3 rounded-lg border border-border p-4 bg-secondary/30">
                <div className="flex items-center gap-2"><Gauge className="h-4 w-4 brand-text" /><p className="text-sm font-medium">AI eligibility review (Claude · advisory)</p></div>
                <Button data-testid="app-run-ai" onClick={runAi} disabled={a.ai_status === "analyzing" || (a.document_files || []).length === 0} className="brand-bg text-white" size="sm">
                  {a.ai_status === "analyzing" ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" />Reviewing…</> : <><Sparkles className="h-4 w-4 mr-1" />{r ? "Re-run review" : "Run AI review"}</>}
                </Button>
              </div>
            )}

            {a.ai_status === "error" && <p className="text-sm text-red-600">AI review failed: {a.ai_error}</p>}

            {canReview && (
              <div className="flex flex-wrap gap-2">
                <Button data-testid="app-copy-link" variant="outline" size="sm" onClick={async () => {
                  try {
                    const { data } = await api.post(`/applications/${appId}/upload-link`);
                    const url = `${window.location.origin}/apply/${data.token}`;
                    await navigator.clipboard.writeText(url).catch(() => {});
                    toast.success("Applicant upload link copied to clipboard");
                  } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
                }}>
                  <LinkIcon className="h-4 w-4 mr-1" />Copy applicant upload link
                </Button>
                {r && (
                  <Button data-testid="app-export-pdf" variant="outline" size="sm" onClick={async () => {
                    try { await downloadReport(`/applications/${appId}/report.pdf`, `admission-report-${a.applicant_name}.pdf`); }
                    catch (e) { toast.error("Could not generate the PDF."); }
                  }}>
                    <Download className="h-4 w-4 mr-1" />Export assessment (PDF)
                  </Button>
                )}
              </div>
            )}

            {r && <AdmissionReport report={r} />}

            {/* Decision */}
            {decided ? (
              <div className="rounded-md border border-border bg-secondary/40 p-4" data-testid="app-decision-final">
                <p className="text-sm font-medium">Decision: <span className="capitalize">{(a.status || "").replaceAll("_", " ")}</span></p>
                {a.decision_notes && <p className="text-sm text-muted-foreground">{a.decision_notes}</p>}
                <p className="text-xs text-muted-foreground mt-1">by {a.decided_by}
                  {a.ai_recommendation && <> · AI advised <span className="capitalize">{a.ai_recommendation.replaceAll("_", " ")}</span> ({a.ai_recommendation_accepted ? "accepted" : "overridden"})</>}
                </p>
              </div>
            ) : canReview ? (
              <div className="rounded-lg border border-border p-4 space-y-3">
                <p className="font-heading font-semibold text-sm">Registrar decision (human-in-the-loop)</p>
                <div className="flex gap-2 flex-wrap">
                  {[["admit", "Admit"], ["request_info", "Request Info"], ["reject", "Reject"]].map(([d, l]) => (
                    <button key={d} data-testid={`app-decide-${d}`} onClick={() => setDecision(d)}
                      className={`px-3 py-1.5 rounded-md text-sm border ${decision === d ? "brand-bg text-white brand-border" : "border-border hover:bg-secondary"}`}>{l}</button>
                  ))}
                </div>
                <Textarea data-testid="app-decision-notes" placeholder="Decision notes…" value={notes} onChange={(e) => setNotes(e.target.value)} />
                {decision === "admit" && (
                  <div data-testid="app-allocate" className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs">Allocate a supervisor (optional — creates a supervision record)</Label>
                      <Button data-testid="app-suggest-sup" size="sm" variant="outline" onClick={() => suggestSup()} disabled={suggesting}>
                        {suggesting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1" />}Suggest best-fit
                      </Button>
                    </div>
                    {suggesting && suggestions.length === 0 && (
                      <p className="text-[11px] text-muted-foreground flex items-center gap-1" data-testid="app-suggest-loading"><Loader2 className="h-3 w-3 animate-spin" />Finding the best-fit supervisor…</p>
                    )}
                    {suggestions.length > 0 && (
                      <div className="space-y-1.5" data-testid="app-suggestions">
                        {suggestions.map((s, idx) => (
                          <button key={s.supervisor_id} type="button" data-testid={`app-suggestion-${s.supervisor_id}`}
                            onClick={() => setSupervisorId(s.supervisor_id)}
                            className={`w-full text-left rounded-md border p-2 text-sm transition-colors ${supervisorId === s.supervisor_id ? "brand-border bg-secondary" : "border-border hover:bg-secondary/50"}`}>
                            <span className="font-medium">{s.name}</span>
                            <Badge variant="outline" className="ml-2 text-[10px] capitalize">{s.match} fit</Badge>
                            {idx === 0 && <Badge className="ml-1 text-[10px] brand-bg text-white">Top pick</Badge>}
                            <span className="block text-xs text-muted-foreground mt-0.5">{s.reason}</span>
                          </button>
                        ))}
                        <p className="text-[11px] text-muted-foreground">AI pre-selected the top match — you decide. Change it above or below anytime.</p>
                      </div>
                    )}
                    <Select value={supervisorId} onValueChange={setSupervisorId}>
                      <SelectTrigger data-testid="app-supervisor-select"><SelectValue placeholder="No supervisor (admit only)" /></SelectTrigger>
                      <SelectContent className="bg-white">
                        {supervisors.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}{s.expertise ? ` · ${s.expertise}` : ""}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <Button data-testid="app-decision-submit" onClick={decide} disabled={!decision} className="brand-bg text-white">
                  {decision === "admit" && supervisorId ? "Admit & Allocate Supervisor" : "Record Decision"}
                </Button>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Awaiting the Registrar's decision.</p>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function AdmissionReport({ report }) {
  const rec = REC[report.recommendation] || { c: "bg-slate-100 text-slate-700", l: report.recommendation };
  return (
    <div className="rounded-lg border brand-border/40 p-4 space-y-4" data-testid="admission-report">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <Badge className={`${rec.c} gap-1 text-sm`}><Sparkles className="h-3.5 w-3.5" />{rec.l}</Badge>
      </div>
      <p className="text-sm text-foreground">{report.summary}</p>
      {report.recommendation_rationale && <p className="text-xs text-muted-foreground">Rationale: {report.recommendation_rationale}</p>}

      {report.eligibility_checklist?.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Eligibility checklist</p>
          <div className="space-y-1.5" data-testid="eligibility-checklist">
            {report.eligibility_checklist.map((c, i) => (
              <div key={i} className="flex items-start gap-2 text-sm">
                {c.met ? <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" /> : <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />}
                <span><span className="font-medium">{c.item}</span>{c.detail ? <span className="text-muted-foreground"> — {c.detail}</span> : null}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {report.documents_reviewed?.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Documents reviewed</p>
          <div className="space-y-1.5">
            {report.documents_reviewed.map((d, i) => (
              <div key={i} className="flex items-start gap-2 text-sm">
                {d.present ? <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" /> : <XCircle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />}
                <span><span className="font-medium">{d.name}</span>{d.notes ? <span className="text-muted-foreground"> — {d.notes}</span> : null}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {report.risk_flags?.length > 0 && (
        <div className="rounded-md border border-red-200 bg-red-50/60 p-3" data-testid="admission-risk-flags">
          <p className="text-xs font-semibold text-red-700 uppercase tracking-wide mb-2 flex items-center gap-1"><ShieldAlert className="h-3.5 w-3.5" />{report.risk_flags.length} risk flag(s) — for human review</p>
          <div className="space-y-2">
            {report.risk_flags.map((f, i) => (
              <div key={i} className="text-xs">
                <p className="font-medium text-red-800">{(f.type || "").replaceAll("_", " ")} · confidence: {f.confidence}</p>
                {f.evidence && <p className="italic text-slate-600">“{f.evidence}”</p>}
                {f.explanation && <p className="text-muted-foreground">{f.explanation}</p>}
              </div>
            ))}
          </div>
        </div>
      )}
      <p className="text-[11px] text-muted-foreground">AI prepares an advisory assessment. The Registrar makes the binding admission decision.</p>
    </div>
  );
}
